//@flow
import React from 'react';
import {
  makeStyles,
  Typography,
  Box,
  Hidden,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from '@material-ui/core';
import {colors} from '../../../../assets/styles/colors';
import {Delete} from '@material-ui/icons';
import {getStringDate} from '../../../../utils/date';
import {useTrainBotActions} from '../../hooks';
import {useToastActions} from '../../../toast-alert/utils';
import {Pagination} from '@material-ui/lab';
import type {
  StoreChatbot,
  ChatbotTraining,
  Pagination as PaginationType,
} from '../../../entities';

const useStyles = makeStyles(theme => ({
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '2rem',
    paddingTop: '2rem',
    borderTop: `1px solid ${colors.grey300}`,
  },
  subtitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.grey800,
  },
  caption: {
    fontSize: '.875rem',
    color: colors.grey800,
  },
  table: {},
  paginationArea: {
    display: 'flex',
    padding: '1rem',
    justifyContent: 'center',
    alignItems: 'center',
  },
  pagination: {},
  trainCard: {
    border: `1px solid ${colors.grey300}`,
    padding: '.75rem',
    borderRadius: '1rem',
  },
  deleteButton: {
    color: colors.danger,
  },
  pdfFileName: {
    fontSize: '.75rem',
    color: colors.grey600,
    marginTop: '.15rem',
    wordBreak: 'break-word',
  },
}));

type Props = {
  chatBot: StoreChatbot,
  training: boolean,
  chatBotTraining: ChatbotTraining[],
  pagination: ?PaginationType,
  page: number,
  setPage: (page: number) => void,
  refetchChatBotTraining: () => void,
};

const TrainingHistory = React.memo(function TrainingHistory(props: Props) {
  const classes = useStyles();
  const {showToast} = useToastActions();
  const {deleteTraining, loading} = useTrainBotActions();
  const {
    chatBotTraining,
    pagination,
    page,
    setPage,
    refetchChatBotTraining,
  } = props;

  const getTrainingTypeLabel = training => {
    if (training.trainingType === 'TEXT') return 'Texto';
    if (training.trainingType === 'PDF') return 'Documento';
    if (training.trainingType === 'TRAINED_MESSAGES') return 'Q&A';
    if (training.trainingType === 'STORE_INFORMATION')
      return 'Información de la tienda';
    if (training.trainingType === 'PRODUCTS') return 'Productos';
    return 'Entrenamiento';
  };

  const getPdfFileName = training => {
    if (training.trainingType !== 'PDF') return '';
    return training.metadata?.values?.PDF?.file_name || '';
  };

  return (
    <>
      <header className={classes.sectionHeader}>
        <Typography className={classes.subtitle}>
          Historial de entrenamiento
        </Typography>
      </header>

      {chatBotTraining.length > 0 ? (
        <>
          <Hidden smDown>
            <TableContainer>
              <Table className={classes.table} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell align="left">Tipo</TableCell>
                    <TableCell align="left">Último entrenamiento</TableCell>
                    <TableCell align="left">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chatBotTraining.map(training => (
                    <TableRow key={training.id}>
                      <TableCell
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          maxWidth: '15ch',
                          textOverflow: 'ellipsis',
                        }}
                        title={training.name}
                      >
                        {training.name}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column">
                          <Typography variant="body2">
                            {getTrainingTypeLabel(training)}
                          </Typography>
                          {training.trainingType === 'PDF' &&
                            getPdfFileName(training) && (
                              <Typography
                                variant="caption"
                                className={classes.pdfFileName}
                              >
                                {getPdfFileName(training)}
                              </Typography>
                            )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {training && getStringDate(training.updatedAt)}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="row">
                          {training.trainingType !== 'PDF' && (
                            <IconButton
                              className={classes.deleteButton}
                              size="small"
                              disabled={loading}
                              onClick={async () => {
                                try {
                                  await deleteTraining(training.id);
                                  showToast({
                                    message:
                                      'Entrenamiento eliminado correctamente',
                                    title: 'Eliminado',
                                    type: 'success',
                                    duration: 3000,
                                  });
                                  refetchChatBotTraining();
                                } catch (error) {
                                  showToast({
                                    message:
                                      'Error al eliminar el entrenamiento',
                                    title: 'Error',
                                    type: 'error',
                                    duration: 3000,
                                  });
                                }
                              }}
                            >
                              <Delete />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Hidden>
          <Hidden mdUp>
            <Box
              display="flex"
              flexDirection="column"
              style={{
                gap: '0.5rem',
              }}
            >
              {chatBotTraining.map(training => (
                <Box
                  display="flex"
                  key={training.id}
                  justifyContent="space-between"
                  alignItems="flex-start"
                  className={classes.trainCard}
                  style={{
                    gap: '0.5rem',
                  }}
                >
                  <Box flex={1}>
                    <Typography
                      variant="subtitle2"
                      style={{
                        fontWeight: 600,
                        color: colors.grey800,
                        marginBottom: '0.25rem',
                        wordBreak: 'break-word',
                      }}
                    >
                      {training.name}
                    </Typography>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap="0.5rem"
                      marginBottom="0.25rem"
                    >
                      <Box
                        sx={{
                          backgroundColor: colors.primary,
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                        }}
                      >
                        {getTrainingTypeLabel(training)}
                      </Box>
                    </Box>
                    {training.trainingType === 'PDF' &&
                      getPdfFileName(training) && (
                        <Typography
                          variant="caption"
                          className={classes.pdfFileName}
                        >
                          {getPdfFileName(training)}
                        </Typography>
                      )}
                    <Typography
                      variant="caption"
                      style={{
                        color: colors.grey600,
                        fontSize: '0.75rem',
                      }}
                    >
                      Último entrenamiento: {getStringDate(training.updatedAt)}
                    </Typography>
                  </Box>
                  {training.trainingType !== 'PDF' && (
                    <IconButton
                      className={classes.deleteButton}
                      size="small"
                      disabled={loading}
                      onClick={async () => {
                        try {
                          await deleteTraining(training.id);
                          showToast({
                            message: 'Entrenamiento eliminado correctamente',
                            title: 'Eliminado',
                            type: 'success',
                            duration: 3000,
                          });
                          refetchChatBotTraining();
                        } catch (error) {
                          showToast({
                            message: 'Error al eliminar el entrenamiento',
                            title: 'Error',
                            type: 'error',
                            duration: 3000,
                          });
                        }
                      }}
                      style={{
                        padding: '4px',
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          </Hidden>
        </>
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={4}
          textAlign="center"
        >
          <Typography variant="h6" className={classes.subtitle} gutterBottom>
            No hay historial de entrenamiento
          </Typography>
          <Typography variant="body2" className={classes.caption}>
            Cuando entrenes tu bot por primera vez, aquí aparecerá el historial
            de todos los entrenamientos realizados.
          </Typography>
        </Box>
      )}

      <div className={classes.paginationArea}>
        <Pagination
          count={
            pagination &&
            (pagination.size >= pagination.total
              ? 1
              : Math.ceil(pagination.total / pagination.size))
          }
          className={classes.pagination}
          color="primary"
          page={page + 1}
          onChange={(e, page) => {
            setPage(page - 1);
          }}
        />
      </div>
    </>
  );
});

export default TrainingHistory;
